import { graphql } from 'gatsby'
import React, { FC, useMemo } from 'react'

import { useSiteMetadataFrom } from '../../../../../hooks/useSiteMetadata'
import { useSiteSettingsFrom } from '../../../../../hooks/useSiteSettings'
import { useSiteSettingsAndMetadata } from '../../../../../hooks/useSiteSettingsAndMetadata'
import { usePractitionerDocumentsNeeded } from '../../../../../lib/practitioner-settings/hooks/usePractitionerDocumentsNeeded'
import { PractitionerSettings } from '../../../../../lib/practitioner-settings/types'
import {
  DocumentOnFileNotReceived,
  DocumentOnFileReceived,
  RequiredForCommissionsKey,
} from '../../../../../lib/practitioner-settings/utils'
import Spacer from '../../../../Spacer'
import { CommissionsOptOutMessage } from '../../dispensary/dashboard/messages/CommissionsOptOutMessage'
import StatusAndSubmitDoc from './StatusAndSubmitDoc'

const reduceEntryToElement = (
  acc: JSX.Element[],
  [documentKey, { formTitle, formUrl, isReceived }]: [
    RequiredForCommissionsKey,
    DocumentOnFileNotReceived | DocumentOnFileReceived,
  ],
): JSX.Element[] => {
  if (acc.length) {
    acc.push(<Spacer size={5} key={`${documentKey}-spacer`} />)
  }
  acc.push(
    <h3 className="small-h3" key={`${documentKey}-header`}>
      {formTitle} Status
    </h3>,
  )
  acc.push(
    <StatusAndSubmitDoc
      key={`${documentKey}-doc`}
      formUrl={formUrl}
      isReceived={isReceived}
      title={`${formTitle} Form`}
    />,
  )
  return acc
}

export interface EftAndTaxSectionProps {
  className?: string
  settings: PractitionerSettings
}

const EftAndTaxSection: FC<EftAndTaxSectionProps> = ({ className, settings }) => {
  const siteSettingsAndMetadata = useSiteSettingsAndMetadata()
  const { commissionsOptOutForm, ...siteSettings } = useSiteSettingsFrom(siteSettingsAndMetadata)
  const { featureFlags } = useSiteMetadataFrom(siteSettingsAndMetadata) ?? {}
  const { commissionsOptOutFormReceived } = settings.practitioner
  const { neededForCommissions, receivedForCommissions } = usePractitionerDocumentsNeeded(
    settings.practitioner,
    siteSettings,
  )

  const neededAndReceivedForCommissionsElements = useMemo(
    () => [
      ...neededForCommissions.reduce(reduceEntryToElement, []),
      ...receivedForCommissions.reduce(reduceEntryToElement, []),
    ],
    [neededForCommissions, receivedForCommissions],
  )

  return (
    <section className={className}>
      {neededAndReceivedForCommissionsElements}
      {!!featureFlags?.commissionsOptOut &&
        !commissionsOptOutFormReceived &&
        !!commissionsOptOutForm?.link && (
          <>
            {!!neededAndReceivedForCommissionsElements.length && <Spacer size={4} />}
            <CommissionsOptOutMessage commissionsOptOutForm={commissionsOptOutForm} />
          </>
        )}
    </section>
  )
}

export const eftFormFragment = graphql`
  fragment eftForm on SanitySiteSettings {
    eftForm {
      label
      link
    }
  }
`

export const taxIdFormFragment = graphql`
  fragment taxIdForm on SanitySiteSettings {
    taxIdFormV2 {
      label
      link
    }
  }
`

export const commissionsOptOutFormFragment = graphql`
  fragment commissionsOptOutForm on SanitySiteSettings {
    commissionsOptOutForm {
      label
      link
    }
  }
`

export default EftAndTaxSection
